import { useChatCoordinator } from 'app/ChatCoordinator';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { useWebsocket } from 'contexts/websocket';
import useAssignmentChatSummary from 'hooks/chat/useAssignmentChatSummary';
import { omit } from 'lodash';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import chatService from 'services/chat';
import submissionService from 'services/submission';
import { hasUnreadChat } from 'utils/chat';
import { getAIPreview } from 'utils/helper';

const PageContext = createContext();

const PageProvider = (props) => {
    const { course, currentAssignmentId, currentAssignment, studentSubmissions, setStudentSubmissions } = useApp();
    const { auth, getAccount } = useAuth();
    const {
        clearPendingOpenChatAssignmentId,
        pendingOpenChatAssignmentId,
        setOpenAssignmentChatId,
        setSubmissionPath,
    } = useChatCoordinator();
    const { closeDialog, setDialog } = useDialog();
    const history = useHistory();
    const { submissionId } = useParams();
    const { message } = useSnackbar();
    const { t } = useTranslation('assignment');
    const { onMessage } = useWebsocket();
    const location = useLocation();

    const [selectedFeedbackId, setSelectedFeedbackId] = useState(null);
    const [isDocumentFullScreen, setIsDocumentFullScreen] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [introductionDialog, setIntroductionDialog] = useState({ open: false });
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [shouldSkipChatOpenAnimation, setShouldSkipChatOpenAnimation] = useState(false);
    const { assignmentChat, setAssignmentChat } = useAssignmentChatSummary({
        assignmentId: currentAssignmentId,
        courseId: auth?.course_id,
        ownerAccountId: auth?.id,
        reconciliationEnabled: !isChatOpen,
    });

    const currentSubmission = studentSubmissions?.find(({ id }) => id === submissionId);
    const currentSubmissionIndex = studentSubmissions
        ?.sort((a, b) => a.created_on - b.created_on)
        ?.findIndex(({ id }) => id === submissionId);

    const hasUnreadSubmissionChat = useMemo(() => {
        return hasUnreadChat({
            chat: assignmentChat,
            accountId: auth?.owner_account_id || auth?.id,
        });
    }, [assignmentChat, auth?.id, auth?.owner_account_id]);

    const getHighlights = useCallback(() => {
        const format = (items) => {
            return (items ?? []).map((item) => {
                return {
                    id: selectedFeedbackId,
                    value: item,
                };
            });
        };

        if (selectedFeedbackId) {
            const feedback = currentSubmission?.ai?.feedback;
            const isRubric = feedback?.feedback_type === 'rubric';
            const suggestion = currentSubmission?.ai?.feedback.feedback?.improvement_suggestions?.find(
                ({ id }) => id === selectedFeedbackId
            );
            const locations = isRubric ? suggestion?.negative?.locations : suggestion?.locations;
            return format(locations);
        }

        return [];
    }, [currentSubmission?.ai?.feedback, selectedFeedbackId]);

    const onClickBack = () => {
        history.push(`/assignments/${currentAssignmentId}/submissions`);
    };

    const onClickInfo = () => {
        setIntroductionDialog({ open: true });
    };

    const onClickPrevAttempt = () => {
        const prevSubmissionId = studentSubmissions[currentSubmissionIndex - 1].id;
        history.push(`/assignments/${currentAssignmentId}/submissions/${prevSubmissionId}`);
    };

    const onClickNextAttempt = () => {
        const nextSubmissionId = studentSubmissions[currentSubmissionIndex + 1].id;
        history.push(`/assignments/${currentAssignmentId}/submissions/${nextSubmissionId}`);
    };

    const onClickRegenerate = () => {
        setDialog({
            open: true,
            message: t('dialog.regenerate-feedback.title', 'Regenerate Feedback?'),
            description: t(
                'dialog.regenerate-feedback.description',
                'This will permanently delete all existing feedback for this draft, and cannot be undone.'
            ),
            note: '',
            variant: 'primary',
            actionButtonText: t('common:controls.regenerate', 'Regenerate'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: async () => {
                closeDialog();
                setIsGenerating(true);
                try {
                    const account = await getAccount();
                    await getAIPreview({
                        target: 'feedback',
                        course_id: course.id,
                        owner_account_id: currentAssignment.owner_account_id,
                        assignment: currentAssignment,
                        submission: currentAssignment,
                        connections: account.connections.map(({ id }) => id),
                        language: course.language ?? 'English',
                    });
                } catch (e) {
                    console.error(e);
                    message.error(
                        t('common:message.something-went-wrong-try-again', 'Something went wrong. Please try again.')
                    );
                }
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const onOpenChat = useCallback(() => {
        setIsChatOpen(true);

        if (!auth?.id || !auth?.course_id || !currentAssignmentId) return;

        const timestamp = Date.now();

        chatService
            .createChat({
                active: true,
                created_on: timestamp,
                modified_on: timestamp,
                first_opened_on: timestamp,
                owner_account_id: auth.id,
                course_id: auth.course_id,
                assignment_id: currentAssignmentId,
                messages: [],
            })
            .then((chat) => {
                setAssignmentChat(chat || null);
            })
            .catch((error) => {
                console.error(error);
            });
    }, [auth?.course_id, auth?.id, currentAssignmentId, setAssignmentChat]);

    const onCloseChat = useCallback(() => {
        setIsChatOpen(false);
    }, []);

    const onAssignmentChatChange = useCallback((chat) => {
        if (!chat) return;
        setAssignmentChat(chat);
    }, [setAssignmentChat]);

    useEffect(() => {
        if (!currentAssignmentId || !submissionId) return;

        setSubmissionPath(
            currentAssignmentId,
            `/assignments/${currentAssignmentId}/submissions/${submissionId}`
        );
    }, [currentAssignmentId, setSubmissionPath, submissionId]);

    useEffect(() => {
        setOpenAssignmentChatId(isChatOpen ? currentAssignmentId || null : null);

        return () => {
            setOpenAssignmentChatId((current) => (current === currentAssignmentId ? null : current));
        };
    }, [currentAssignmentId, isChatOpen, setOpenAssignmentChatId]);

    useEffect(() => {
        if (currentSubmission) {
            setSelectedFeedbackId(currentSubmission.ai.feedback?.improvement_suggestions?.[0]?.id);
        }
    }, [currentSubmission]);

    useEffect(() => {
        const cleanup = onMessage(async (websocketMessage) => {
            if (websocketMessage.data?.payload_type === 'regenerate_submission') {
                const { submission } = websocketMessage.data.payload;
                const updatedSubmission = {
                    ...currentSubmission,
                    modified_on: new Date().getTime(),
                };
                if (isGenerating) {
                    updatedSubmission.ai.grading = submission.data.grading;
                    updatedSubmission.ai.feedback = submission.data.feedback;
                }
                setIsGenerating(false);
                setStudentSubmissions((current) => {
                    return current.map((submission) => {
                        if (submission.id !== updatedSubmission.id) return submission;
                        submissionService.updateSubmission(updatedSubmission.id, omit(updatedSubmission, ['_id']));
                        return updatedSubmission;
                    });
                });
            }
        });
        return cleanup;
    }, [currentSubmission, isGenerating, onMessage, setStudentSubmissions]);

    useEffect(() => {
        const shouldOpenFromRouteState = Boolean(location.state?.openChat);
        const shouldOpenFromPendingAssignment = pendingOpenChatAssignmentId === currentAssignmentId;

        if ((!shouldOpenFromRouteState && !shouldOpenFromPendingAssignment) || isChatOpen) return;

        setShouldSkipChatOpenAnimation(true);
        onOpenChat();
        clearPendingOpenChatAssignmentId();
        history.replace({
            pathname: location.pathname,
            search: location.search,
            hash: location.hash,
            state: undefined,
        });
    }, [
        currentAssignmentId,
        clearPendingOpenChatAssignmentId,
        history,
        isChatOpen,
        location.hash,
        location.pathname,
        location.search,
        location.state,
        onOpenChat,
        pendingOpenChatAssignmentId,
    ]);

    useEffect(() => {
        if (!shouldSkipChatOpenAnimation || !isChatOpen) return;

        const timeoutId = setTimeout(() => {
            setShouldSkipChatOpenAnimation(false);
        }, 0);

        return () => clearTimeout(timeoutId);
    }, [isChatOpen, shouldSkipChatOpenAnimation]);

    return (
        <PageContext.Provider
            value={{
                selectedFeedbackId,
                setSelectedFeedbackId,
                isDocumentFullScreen,
                setIsDocumentFullScreen,
                isGenerating,
                setIsGenerating,
                introductionDialog,
                setIntroductionDialog,
                currentSubmission,
                currentSubmissionIndex,
                highlights: getHighlights(),
                hasUnreadSubmissionChat,
                isChatOpen,
                shouldSkipChatOpenAnimation,
                onClickBack,
                onClickInfo,
                onClickPrevAttempt,
                onClickNextAttempt,
                onClickRegenerate,
                onAssignmentChatChange,
                onCloseChat,
                onOpenChat,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
